/** ***********
*
* <user-right.reducer.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { ParentFamilyRight } from 'src/app/models/family';
import { User } from 'src/app/models/user';
import { FamilyManagers, Group, RightRequest, RequestAGroup } from 'src/app/models/user-right';
import { cloneDeep } from 'lodash';
import * as featureActions from '../actions/user-right.action';
import { createReducer, on } from '@ngrx/store';

export interface State {
    data: {
        usersRightTree: ParentFamilyRight[],
        myRightTree: ParentFamilyRight[],
        managers?: FamilyManagers[],
        rightRequests?: RightRequest[];
        allUsers?: User[];
        groups?: Group[];
        userGroups?: (Partial<Group> & Required<Pick<Group, 'id' | 'name'>> & Pick<RequestAGroup, 'managerEmail'> & { requested?: boolean })[];
        listOfGroups?: (Partial<Group> & Required<Pick<Group, 'id' | 'name'>>)[];
    };
    loading: boolean;
    loaded: boolean;
    requestsLoaded: boolean;
    groupsLoaded?: boolean;
    groupUsersLoading?: boolean;
    groupFamiliesLoading?: boolean;
    usersLoaded?: boolean;
}
// default state
const initialState: State = {
    data: {
        usersRightTree: [],
        myRightTree: [],
        managers: [],
        rightRequests: [],
        allUsers: [],
        groups: [],
        userGroups: [],
        listOfGroups: [],
    },
    loading: false,
    loaded: false,
    requestsLoaded: false,
    groupsLoaded: false,
    groupUsersLoading: false,
    groupFamiliesLoading: false,
    usersLoaded: false
};

const userRightReducer = createReducer(initialState,
    on(featureActions.transferGroupOwnership, (state): State => {
        state.loading = true;
        return {...state};
    }),
    on(featureActions.successTransferGroupOwnership, (state, action): State => {
        state.groupsLoaded = true;
        state.loading = false;
        state.data.groups = state.data.groups.map(
            (group) => group.id === action.group.id ? action.group : group
        );
        return {...state};
    }),
)

export function reducer(
    state: State = initialState,
    action: featureActions.Actions
): State {

    let datas: {
        usersRightTree: ParentFamilyRight[],
        myRightTree: ParentFamilyRight[],
        managers?: FamilyManagers[],
        rightRequests?: RightRequest[];
        allUsers?: User[];
        requstedGroup?: RequestAGroup[];
    } = {
        usersRightTree: [],
        myRightTree: [],
        managers: [],
        rightRequests: [],
        allUsers: [],
        requstedGroup: [],
    };

    switch (action.type) {
        case featureActions.ActionTypes.LOAD_MY_RIGHT_TREE:
            return {
                ...state,
                loading: true
            };

        case featureActions.ActionTypes.SUCCESS_MY_RIGHTS_TREE:
            return {
                ...state,
                loading: false,
                loaded: true,
                data: (() => {
                    datas = state.data;
                    /* Set the list of parent families mapped with pending requests */
                    const parentFamiliesRights = action.parentFamiliesRights;
                    const familiesRights = action.familiesRigths;
                    const parentFamiliesMap: { [id: string]: ParentFamilyRight } = {};
                    // prepare a mapped object in order to improve performances
                    parentFamiliesRights.forEach((parentFamilyRight) => {
                        parentFamilyRight.families = [];
                        parentFamiliesMap[parentFamilyRight.id] = parentFamilyRight;
                    });
                    // push each family into its corresponding parent
                    familiesRights.forEach((family) => {
                        const parent = parentFamiliesMap[family.parentFamilyId];
                        if (parent) {
                            parent.families.push(family);
                        }
                    });
                    datas.myRightTree = parentFamiliesRights;
                    return datas;
                })()
            };

        case featureActions.ActionTypes.UPDATE_MY_RIGHTS_TREE_REQUESTS_MAPPING:
            return {
                ...state,
                loading: false,
                loaded: true,
                data: (() => {

                    datas = state.data;
                    datas.myRightTree = state.data.myRightTree.slice();
                    /* Refresh the list of parent families when the right requests list has changed */
                    const rightRequests = action.payload;
                    const parentFamiliesRights = datas.myRightTree;

                    // map the requests with the parent families and families
                    if (rightRequests) {
                        parentFamiliesRights.forEach(
                            (parentFamilyRight) => {
                                const correspondingRequest = rightRequests.find(request => (request.parentFamilyId === parentFamilyRight.id) && !request.familyId);
                                parentFamilyRight.manager = {
                                    email: correspondingRequest?.managerMail,
                                    id: correspondingRequest?.managerId,
                                    userId: correspondingRequest?.managerUserId,
                                    firstname: correspondingRequest?.managerFirstname,
                                    lastname: correspondingRequest?.managerLastname
                                };
                                parentFamilyRight.requestedLevel = correspondingRequest?.level;
                                parentFamilyRight.requestId = correspondingRequest?.id;

                                if (parentFamilyRight.families) {

                                    parentFamilyRight.families.forEach((family) => {
                                        const _correspondingRequest = rightRequests.find(request => request.familyId === family.id);
                                        family.manager = {
                                            email: _correspondingRequest?.managerMail,
                                            id: _correspondingRequest?.managerId,
                                            userId: _correspondingRequest?.managerUserId,
                                            firstname: _correspondingRequest?.managerFirstname,
                                            lastname: _correspondingRequest?.managerLastname
                                        };
                                        family.requestedLevel = _correspondingRequest?.level;
                                        family.requestId = _correspondingRequest?.id;

                                    });
                                }
                            }
                        );
                    }
                    return datas;
                })()
            };

        case featureActions.ActionTypes.SUCCESS_USER_RIGHT_TREE:
            return {
                ...state,
                loading: false,
                loaded: true,
                data: (() => {
                    datas = state.data;
                    datas.usersRightTree = state.data.usersRightTree.slice();
                    if (action.parentsRights.length !== 0) {
                        const userId = action.parentsRights[0].userId;
                        // remove parent family right data for the specified user
                        // loop backward in order to avoid index issue during deletion
                        for (let i = datas.usersRightTree.length - 1; i >= 0; i--) {
                            const parentFamily = datas.usersRightTree[i];
                            if (parentFamily.userId.toLowerCase() === userId.toLowerCase()) {
                                datas.usersRightTree.splice(i, 1);
                            }
                        }
                    }
                    // Build parent families and families tree
                    const parentRights = action.parentsRights;
                    const familiesRight = action.familiesRights;
                    const parentFamiliesMap: { [id: string]: ParentFamilyRight } = {};
                    for (const parent of parentRights) {
                        parentFamiliesMap[parent.id] = parent;
                        parent.families = familiesRight.filter(family => family.parentFamilyId === parent.id);
                    }
                    familiesRight.forEach((family) => {
                        const parent = parentFamiliesMap[family.parentFamilyId];
                        if (parent) {
                            family.parentFamily = parent;
                        }
                    });
                    datas.usersRightTree.push(...parentRights);
                    return datas;
                })()
            };

        case featureActions.ActionTypes.ERROR_MY_RIGHT_TREE:
            return {
                ...state,
                loading: false
            };

        case featureActions.ActionTypes.SUCCESS_UPDATE_RIGHT:
            return {
                ...state,
                loading: false,
                loaded: true,
                data: (() => {
                    datas = cloneDeep(state.data);
                    for (const item of action.payload) {
                        const parentFamilyIdToUpdate = item.parentFamilyId;
                        const parentFamilyLevelToSet = item.parentFamilyLevel;
                        const familyIdToUpdate = item.familyId;
                        const familyLevelToSet = item.familyLevel;
                        const userId = item.userId;

                        // update expanded data to be false
                        datas.usersRightTree.forEach((items) => {
                            return items.expanded = false;
                        });

                        // Parent family
                        const parentFamilyToUpdate = datas.usersRightTree?.find(
                            (parentFamily) => {
                                return (parentFamily.id === parentFamilyIdToUpdate && parentFamily.userId === userId);
                            }
                        );

                        if (parentFamilyToUpdate) { // not the case for manageFamilyUsers screen
                            if (!familyIdToUpdate) { // if no family ID, this means that the role is set to a parent
                                parentFamilyToUpdate.level = parentFamilyLevelToSet;
                            }

                            // when a role is removed from a parent family, we set the basic role for all the families inside this parent
                            if (parentFamilyToUpdate?.families && parentFamilyLevelToSet === 'basic') {
                                parentFamilyToUpdate.families.forEach(
                                    (family) => family.level = parentFamilyLevelToSet
                                );
                            }

                            // family
                            if (familyIdToUpdate && familyLevelToSet && parentFamilyToUpdate?.families) {
                                const familyToUpdate = parentFamilyToUpdate.families.find(family => family.id === familyIdToUpdate);
                                if (familyToUpdate) {
                                    familyToUpdate.level = familyLevelToSet;
                                }
                            }

                            parentFamilyToUpdate.expanded = item.expanded;
                        }

                    }
                    return datas;
                })()
            };

        case featureActions.ActionTypes.SUCCESS_MANAGERS:

            return {
                ...state,
                loading: false,
                loaded: true,
                data: (() => {
                    datas = state.data;
                    datas.managers = state.data.managers.slice();

                    const familyId = action.payload.familyId;
                    const managers = action.payload.managers;

                    const managersItem = datas.managers.find(el => el.familyId === familyId);
                    if (managersItem) {
                        managersItem.managers = managers;
                    } else {
                        datas.managers.push({
                            familyId: familyId,
                            managers: managers
                        });
                    }

                    return datas;
                })()
            };

        case featureActions.ActionTypes.CREATE_RIGHT_REQUEST:
            return {
                ...state,
                loading: false,
                loaded: true,
                data: (() => {
                    datas = state.data;
                    datas.rightRequests = state.data.rightRequests.slice();

                    datas.rightRequests.push(action.payload);
                    return datas;
                })()
            };

        case featureActions.ActionTypes.DELETE_RIGHT_REQUEST:
            return {
                ...state,
                loading: false,
                loaded: true,
                data: (() => {
                    datas = state.data;
                    datas.rightRequests = state.data.rightRequests.slice();

                    const requestToDelete = datas.rightRequests.find((request) => request.id === action.payload);
                    const requestToDeleteIndex = datas.rightRequests.indexOf(requestToDelete);

                    // delete the array item
                    datas.rightRequests.splice(requestToDeleteIndex, 1);

                    return datas;
                })()
            };

        case featureActions.ActionTypes.SUCCESS_RIGHT_REQUEST:
            return {
                ...state,
                loading: false,
                loaded: true,
                requestsLoaded: true,
                data: (() => {
                    datas = state.data;
                    datas.rightRequests = action.payload;
                    return datas;
                })()
            };

        case featureActions.ActionTypes.SUCCESS_ALL_USERS:
            return {
                ...state,
                loading: false,
                loaded: true,
                usersLoaded: true,
                data: (() => {
                    datas = state.data;
                    datas.allUsers = action.payload;
                    return datas;
                })()
            };
        case featureActions.ActionTypes.SUCCESS_UPDATE_SPECIFIC_ROLE:
            return {
                ...state,
                loading: false,
                loaded: true,
                data: (() => {
                    datas = state.data;
                    const userToUpdate = action.payload;
                    const userIndex = datas.allUsers.findIndex(_user => _user.id === userToUpdate.id);
                    if (userIndex > -1) {
                        datas.allUsers[userIndex] = userToUpdate;
                    }
                    return datas;
                })()
            };

        case featureActions.ActionTypes.FETCH_GROUPS:
        case featureActions.ActionTypes.FETCH_LIST_OF_ALL_GROUPS:
          return {
            ...state,
            groupsLoaded: false,
          };

        case featureActions.ActionTypes.SUCCESS_FETCH_GROUPS:
          return {
            ...state,
            groupsLoaded: true,
            data: {
              ...state.data,
              groups: action.payload,
            },
          };

        case featureActions.ActionTypes.ERROR_FETCH_GROUPS:
        case featureActions.ActionTypes.ERROR_UPDATE_GROUP:
        case featureActions.ActionTypes.ERROR_CREATE_GROUP:
        case featureActions.ActionTypes.ERROR_DELETE_GROUP:
          return {
            ...state,
            groupsLoaded: true,
          };

        case featureActions.ActionTypes.SUCCESS_UPDATE_GROUP:
          return {
            ...state,
            groupsLoaded: true,
            data: {
              ...state.data,
              groups: state.data.groups.map(
                group => {
                    if (action.payload.id === group.id) {
                        return action.payload;
                    }
                    return group;
                }
              ),
            },
          };

        case featureActions.ActionTypes.SUCCESS_CREATE_GROUP:
          return {
            ...state,
            groupsLoaded: true,
            data: {
              ...state.data,
              groups: state.data.groups.concat([action.payload]),
            },
          };

        case featureActions.ActionTypes.SUCCESS_DELETE_GROUP:
          return {
            ...state,
            groupsLoaded: true,
            data: {
              ...state.data,
              groups: state.data.groups.filter(
                group => group.id !== action.payload.groupId
              ),
            },
          };

        case featureActions.ActionTypes.FETCH_USERS_OF_GROUP:
        case featureActions.ActionTypes.ADD_USERS_TO_GROUP:
        case featureActions.ActionTypes.REMOVE_USERS_FROM_GROUP:
          return {
            ...state,
            groupUsersLoading: true,
          };

        case featureActions.ActionTypes.FETCH_FAMILIES_OF_GROUP:
        case featureActions.ActionTypes.ADD_FAMILIES_TO_GROUP:
        case featureActions.ActionTypes.CHANGE_GROUP_FAMILIES_LEVEL:
        case featureActions.ActionTypes.REMOVE_FAMILIES_FROM_GROUP:
          return {
            ...state,
            groupFamiliesLoading: true,
          };

        case featureActions.ActionTypes.SUCCESS_FETCH_USERS_OF_GROUP:
        case featureActions.ActionTypes.SUCCESS_ADD_USERS_TO_GROUP:
        case featureActions.ActionTypes.SUCCESS_REMOVE_USERS_FROM_GROUP:
          // eslint-disable-next-line no-case-declarations
          const userGroups = state.data.userGroups;
          if (userGroups) {
            switch (action.type) {
              case featureActions.ActionTypes.SUCCESS_ADD_USERS_TO_GROUP:
                userGroups.push({ id: action.payload.group.id, name: action.payload.group.name });
                break;
              case featureActions.ActionTypes.SUCCESS_REMOVE_USERS_FROM_GROUP:
                userGroups.splice(
                  userGroups.findIndex(
                    ({ id }) => action.payload.group.id === id
                  ),
                  1,
                );
                break;
            }
          }
          return {
            ...state,
            groupUsersLoading: false,
            data: {
              ...state.data,
              groups: state.data.groups.map(
                group => {
                  if (action.payload.group.id === group.id) {
                    return {
                      ...group,
                      quantityOfUsers: action.payload.users?.length ?? group.quantityOfUsers,
                      users: action.payload.users,
                    };
                  }
                  return group;
                }
              ),
              userGroups,
            },
          };

          case featureActions.ActionTypes.SUCCESS_FETCH_FAMILIES_OF_GROUP:
          case featureActions.ActionTypes.SUCCESS_ADD_FAMILIES_TO_GROUP:
          case featureActions.ActionTypes.SUCCESS_REMOVE_FAMILIES_FROM_GROUP:
            return {
              ...state,
              groupFamiliesLoading: false,
              data: {
                ...state.data,
                groups: state.data.groups.map(
                  group => {
                    if (action.payload.group.id === group.id) {
                      return {
                        ...group,
                        quantityOfFamilies: action.payload.families?.length ?? group.quantityOfFamilies,
                        families: action.payload.families,
                      };
                    }
                    return group;
                  }
                ),
              },
            };

          case featureActions.ActionTypes.SUCCESS_CHANGE_GROUP_FAMILIES_LEVEL:
            return {
              ...state,
              groupFamiliesLoading: false,
              data: {
                ...state.data,
                groups: state.data.groups.map(
                  group => {
                    if (action.payload.group.id === group.id) {
                      group.families = group.families.map(
                        family => {
                          const updatedFamily = action.payload.families.find(
                            ({ id }) => id === family.id
                          );
                          return updatedFamily ?? family;
                        }
                      );
                    }
                    return group;
                  }
                ),
              },
            };

        case featureActions.ActionTypes.ERROR_FETCH_USERS_OF_GROUP:
        case featureActions.ActionTypes.ERROR_REMOVE_USERS_FROM_GROUP:
          return {
            ...state,
            groupUsersLoading: false,
          };

        case featureActions.ActionTypes.ERROR_FETCH_FAMILIES_OF_GROUP:
        case featureActions.ActionTypes.ERROR_CHANGE_GROUP_FAMILIES_LEVEL:
        case featureActions.ActionTypes.ERROR_REMOVE_FAMILIES_FROM_GROUP:
          return {
            ...state,
            groupFamiliesLoading: false,
          };

        case featureActions.ActionTypes.ERROR_ADD_USERS_TO_GROUP:
            return {
                ...state,
                groupUsersLoading: false,
                groupFamiliesLoading: false,
            };

        case featureActions.ActionTypes.SUCCESS_FETCH_GROUPS_OF_USER:
          // eslint-disable-next-line no-case-declarations
          const userGroup: State['data']['userGroups'] = [];
          if (action.payload.group) {
            userGroup.push({
                id: action.payload.group.id,
                name: action.payload.group.name,
            });
          }
          if (action.payload.requestedGroup) {
            userGroup.push({
                id: action.payload.requestedGroup.id,
                name: action.payload.requestedGroup.groupName,
                managerEmail: action.payload.requestedGroup.managerEmail,
                requested: true,
            });
          }
          return {
            ...state,
            data: {
              ...state.data,
              userGroups: userGroup,
            }
          };

        case featureActions.ActionTypes.SUCCESS_FETCH_LIST_OF_ALL_GROUPS:
          return {
            ...state,
            groupsLoaded: true,
            data: {
              ...state.data,
              listOfGroups: action.payload.group ? action.payload.group.map(
                ({ id, name }) =>  ({ id, name })
              ) : [],
            }
          };

        case featureActions.ActionTypes.SUCCESS_RAISE_GROUP_REQUEST:
            return {
                ...state,
                data: {
                    ...state.data,
                    userGroups: state.data.userGroups.concat({
                        id: action.payload.requestedGroup.id,
                        name: action.payload.requestedGroup.groupName,
                        requested: true,
                        managerEmail: action.payload.requestedGroup.managerEmail,
                    }),
                }
            };

        case featureActions.ActionTypes.SUCCESS_CANCEL_GROUP_REQUEST:
            return {
                ...state,
                data: {
                    ...state.data,
                    userGroups: state.data.userGroups.filter(
                        ({ id }) => id !== action.payload.groupData.id
                    ),
                },
            };

        case featureActions.ActionTypes.SUCCESS_DELETE_MY_RIGHT:
          return {
            ...state,
            data: (() => {
              datas = state.data;
              datas.myRightTree = state.data.myRightTree.slice();
              /* Refresh the list of parent families when the right requests list has changed */
              const parentFamiliesRights = datas.myRightTree;
              const removeRoleData = action.payload.removeRight;
              if (removeRoleData.isParentFamily) {
                const pfIndex = parentFamiliesRights.findIndex(_pfamily => _pfamily.id === removeRoleData.deleteMyRight.id);
                if (pfIndex > -1) {
                  parentFamiliesRights[pfIndex].level = 'basic';
                }
                parentFamiliesRights[pfIndex]?.families.map((family) => {
                  family.level = 'basic';
                });
              } else {
                const pfIndex = parentFamiliesRights.findIndex(_pfamily => _pfamily.id === removeRoleData.deleteMyRight.parentFamilyId);
                const familyIndex = parentFamiliesRights[pfIndex].families.findIndex((family) => family.id === removeRoleData.deleteMyRight.id);
                if (familyIndex > -1) {
                  parentFamiliesRights[pfIndex].families[familyIndex].level = 'basic';
                }
              }
              return datas;
          })()
        };


        default:
            return userRightReducer(state, action);

    }

}
